import PropTypes from "prop-types";
import Lomake from "@/components/02-organisms/Lomake";
import { useChangeObjectsByAnchorWithoutUnderRemoval } from "@/stores/muutokset";
import * as R from "ramda";
import { useIntl } from "react-intl";
import common from "@/i18n/definitions/common.js";
import { useEffect } from "react";

const constants = {
  formLocation: ["kielet", "tutkintokielet"]
};

const Koulutusala = ({
  aktiivisetTutkinnot,
  koulutusalakoodiarvo,
  maaraykset,
  mode,
  sectionId,
  title
}) => {
  const intl = useIntl();

  const createAllEnglishKieletChangeObjects = tutkinnot => {
    return R.map(tutkinto => {
      // Extract the relevant part of the anchor
      const anchorParts = R.split(".", tutkinto.anchor);
      const relevantPart = R.join(".", R.slice(1, 3, anchorParts)); // Get parts ex. 02.1.321141
      const anchor = `${sectionId}.${relevantPart}.kielet`;
      if (isMaaraysSelected(anchor)) {
        let selectedMaarays = getSelectedMaaraysFor(anchor);
        if (selectedMaarays.properties && selectedMaarays.properties.value) {
          let eng = {
            label: "englanti", // You can modify this as needed
            value: "EN" // You can modify this as needed
          };
          if (!R.any(R.equals(eng), selectedMaarays.properties.value)) {
            selectedMaarays.properties.value.push(eng); // lisää olemassa oleva valinta ettei se katoa
          }
          return selectedMaarays;
        } else if (
          selectedMaarays &&
          selectedMaarays.length > 0 &&
          selectedMaarays[0].koodisto === "kieli" &&
          selectedMaarays[0].koodi &&
          selectedMaarays[0].koodi.koodiArvo
        ) {
          const kielikoodi = R.path(["koodi", "koodiArvo"], selectedMaarays[0]);

          const label = R.pipe(
            R.path(["koodi", "metadata"]),
            R.find(R.propEq("kieli", kielikoodi)),
            R.prop("nimi")
          )(selectedMaarays[0]);

          const langArr = [
            ...(kielikoodi !== "EN" ? [{ label, value: kielikoodi }] : []),
            { label: "englanti", value: "EN" }
          ];

          return {
            anchor: anchor,
            properties: {
              value: langArr
            }
          };
        }
      }
      // Create the new object structure
      return {
        anchor: anchor,
        properties: {
          value: [
            {
              label: "englanti", // You can modify this as needed
              value: "EN" // You can modify this as needed
            }
          ]
        }
      };
    }, tutkinnot);
  };

  const onDeselectAll = tutkinnot => {
    return R.map(tutkinto => {
      // Extract the relevant part of the anchor
      const anchorParts = R.split(".", tutkinto.anchor);
      const relevantPart = R.join(".", R.slice(1, 3, anchorParts)); // Get parts ex. 02.1.321141
      const anchor = `${sectionId}.${relevantPart}.kielet`;
      if (isMaaraysSelected(anchor)) {
        return null;
      }

      // Create the new object structure
      return {
        anchor: anchor,
        properties: {
          value: []
        }
      };
    }, tutkinnot);
  };

  const [changeObjects] = useChangeObjectsByAnchorWithoutUnderRemoval({
    anchor: sectionId
  });

  const tutkintomaaraykset = R.pipe(R.filter(R.propEq("koodisto", "koulutus")))(
    maaraykset
  );

  /**
   * Tarkistetaan onko edellisellä luvalla tutkintomääräystä, jossa tutkintokieli valittuna, ei ota kantaa lomakkeen valintoihin.
   * @param targetAnchor
   * @returns {boolean}
   */
  const isMaaraysSelected = targetAnchor => {
    const koodiarvo = targetAnchor.split(".").slice(2, 3).join(".");
    for (const tutkinto of tutkintomaaraykset) {
      const { koodi } = tutkinto;
      if (koodi && koodi.koodiArvo === koodiarvo) {
        const koodisto = koodi.koodisto;
        if (koodisto?.koodistoUri === "koulutus") {
          const hasTutkintokieliRajoite = hasTutkintokieletRajoiteChangeObjects(
            tutkinto,
            targetAnchor
          );

          const hasKieletRajoite = hasTutkintoKieletRajoite(tutkinto);

          if (hasTutkintokieliRajoite || hasKieletRajoite) {
            return true;
          }
        }
      }
    }

    return false;
  };

  const getSelectedMaaraysFor = targetAnchor => {
    const koodiarvo = targetAnchor.split(".").slice(2, 3).join(".");
    for (const tutkinto of tutkintomaaraykset) {
      const { koodi } = tutkinto;
      if (koodi && koodi.koodiArvo === koodiarvo) {
        const koodisto = koodi.koodisto;
        if (koodisto?.koodistoUri === "koulutus") {
          const tutkintokieliRajoite = getTutkintokieletRajoiteChangeObjects(
            tutkinto,
            targetAnchor
          );

          const kieletRajoite = getTutkintoKieletRajoite(tutkinto);

          return tutkintokieliRajoite || kieletRajoite;
        }
      }
    }

    return false;
  };

  const getAliMaaraykset = tutkinto => {
    return R.ifElse(
      R.has("aliMaaraykset"),
      R.prop("aliMaaraykset"),
      R.always([])
    )(tutkinto);
  };

  /**
   * Testataan löytyykö tutkintomääräykseltä tutkintokielirajoitetta. Palauttaa tosi mikäli määräyksen alimääräyksistä löydetään polusta meta.changgeObjects targetAnchoria vastaava objekti.
   * @param tutkinto tutkintokielimääräys
   * @param targetAnchor esim. kielet_tutkintokielet_04.1.331101.kielet
   * @returns {Boolean}
   */
  const hasTutkintokieletRajoiteChangeObjects = (tutkinto, targetAnchor) => {
    const aliMaaraykset = getAliMaaraykset(tutkinto);
    if (!Array.isArray(aliMaaraykset) || aliMaaraykset.length === 0) {
      return false;
    }

    return R.pipe(
      R.filter(
        R.allPass([
          R.propEq("meta", R.__),
          R.pipe(R.prop("meta"), R.isEmpty, R.not)
        ])
      ),
      R.map(R.pathOr([], ["meta", "changeObjects"])),
      R.filter(R.is(Array)),
      R.flatten,
      R.find(R.propEq("anchor", targetAnchor)),
      R.isEmpty,
      R.not
    )(aliMaaraykset);
  };

  const getTutkintokieletRajoiteChangeObjects = (tutkinto, targetAnchor) => {
    const aliMaaraykset = getAliMaaraykset(tutkinto);
    if (!Array.isArray(aliMaaraykset) || aliMaaraykset.length === 0) {
      return false;
    }

    return R.pipe(
      R.filter(
        R.allPass([
          R.propEq("meta", R.__),
          R.pipe(R.prop("meta"), R.isEmpty, R.not)
        ])
      ),
      R.map(R.pathOr([], ["meta", "changeObjects"])),
      R.filter(R.is(Array)),
      R.flatten,
      R.find(R.propEq("anchor", targetAnchor))
    )(aliMaaraykset);
  };

  /**
   * Testataan löytyykö tutkintomääräykseltä tutkintokielirajoitetta. Palauttaa tosi mikäli määräyksen alimääräyksistä löydetään koodisto kieli, ja koodiArvo, joka on stringi. Kielirajoitteen koodiArvo voi olla FI,EN jne.
   * @param tutkinto tutkintokielimääräys
   * @returns {boolean}
   */
  const hasTutkintoKieletRajoite = tutkinto => {
    return R.pipe(
      getAliMaaraykset,
      R.filter(
        R.allPass([
          R.pipe(
            R.prop("koodi"),
            R.prop("koodisto"),
            R.prop("koodistoUri"),
            R.equals("kieli")
          ),
          R.pipe(R.prop("koodi"), R.prop("koodiArvo"), R.is(String))
        ])
      ),
      R.isEmpty,
      R.not
    )(tutkinto);
  };

  const getTutkintoKieletRajoite = tutkinto => {
    return R.pipe(
      getAliMaaraykset,
      R.filter(
        R.allPass([
          R.pipe(
            R.prop("koodi"),
            R.prop("koodisto"),
            R.prop("koodistoUri"),
            R.equals("kieli")
          ),
          R.pipe(R.prop("koodi"), R.prop("koodiArvo"), R.is(String))
        ])
      )
    )(tutkinto);
  };

  const lomakedata =
    mode === "modification"
      ? { aktiiviset: aktiivisetTutkinnot }
      : { koulutusalakoodiarvo, maaraykset };
  return (
    <Lomake
      aktiivisetTutkinnot={aktiivisetTutkinnot}
      onDeselectAll={onDeselectAll}
      onSelectAll={createAllEnglishKieletChangeObjects}
      selectAll={true}
      selectAllText={intl.formatMessage(common.selectalllangsbtn)}
      anchor={sectionId}
      changeObjects={changeObjects}
      data={lomakedata}
      isRowExpanded={mode === "reasoning"}
      mode={mode}
      path={constants.formLocation}
      rowTitle={title}
      showCategoryTitles={true}
    />
  );
};

Koulutusala.propTypes = {
  aktiivisetTutkinnot: PropTypes.array,
  koodiarvo: PropTypes.string,
  koulutusalakoodiarvo: PropTypes.string,
  maaraykset: PropTypes.array,
  mode: PropTypes.string,
  sectionId: PropTypes.string,
  title: PropTypes.string
};

export default Koulutusala;
